import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { IScenario } from 'commonTypes/api/scenarios/scenario';

import { fetchOffersDataApi } from 'shared/api/offers';
import { getStatusesAPI } from 'shared/api/statuses';

import { getOfferDisbursementStepThunk, goToNextScenarioThunk, startScenarioThunk } from 'entities/scenario';

import { prepareScenarioPayload } from '../lib/prepareScenarioPayload';

import {
  setMplOffersAlreadySentFromSameBank,
  setMplOffersStatusesData,
  setMplOfferStatus,
  setMplOfferStatusesFromRankingOffers,
} from './reducers';
import type { CreditFullDealScreen } from './types';

export interface ScenarioState {
  currentStep: IScenario | null;
  isLoading: boolean;
  isError: boolean;
  displayedScreen: CreditFullDealScreen | null;
  currentStepOpenedAt: number | null;
}

const initialState: ScenarioState = {
  currentStep: null,
  isLoading: false,
  isError: false,
  displayedScreen: null,
  currentStepOpenedAt: null,
};

const setLoadingStart = (state: ScenarioState) => {
  state.isLoading = true;
  state.isError = false;
};

const setLoadingSuccess = (state: ScenarioState) => {
  state.isLoading = false;
};

const setLoadingError = (state: ScenarioState) => {
  state.isLoading = false;
  state.isError = true;
};

export const scenarioSlice = createSlice({
  name: 'scenario',
  initialState,
  reducers: {
    setScenario: {
      reducer: (state, action: PayloadAction<IScenario>) => {
        state.currentStep = action.payload;
        state.isLoading = false;
        state.currentStepOpenedAt = Date.now();
      },
      prepare: prepareScenarioPayload,
    },
    setDisplayedScreen: (state, action: PayloadAction<CreditFullDealScreen>) => {
      state.displayedScreen = action.payload;
    },
    clearScenario: (state) => {
      state.currentStep = null;
      state.isLoading = false;
      state.isError = false;
      state.currentStepOpenedAt = null;
    },
    currentStepClosed: (state) => {
      state.currentStepOpenedAt = null;
    },
    setMplPreApprovalOfferStatus: setMplOfferStatus,
  },
  extraReducers: (builder) => {
    builder.addCase(goToNextScenarioThunk.pending, setLoadingStart);
    builder.addCase(goToNextScenarioThunk.fulfilled, (state) => {
      setLoadingSuccess(state);
      setMplOffersAlreadySentFromSameBank(state);
    });
    builder.addCase(goToNextScenarioThunk.rejected, setLoadingError);

    builder.addCase(startScenarioThunk.pending, setLoadingStart);
    builder.addCase(startScenarioThunk.fulfilled, (state) => {
      setLoadingSuccess(state);
      setMplOffersAlreadySentFromSameBank(state);
    });
    builder.addCase(startScenarioThunk.rejected, setLoadingError);

    builder.addCase(getOfferDisbursementStepThunk.pending, setLoadingStart);
    builder.addCase(getOfferDisbursementStepThunk.fulfilled, setLoadingSuccess);
    builder.addCase(getOfferDisbursementStepThunk.rejected, setLoadingError);

    builder.addMatcher(getStatusesAPI.endpoints.getStatuses.matchFulfilled, setMplOffersStatusesData);
    builder.addMatcher(fetchOffersDataApi.endpoints.getOffers.matchFulfilled, setMplOfferStatusesFromRankingOffers);
  },
});

export const { setScenario, setMplPreApprovalOfferStatus, setDisplayedScreen, clearScenario, currentStepClosed } =
  scenarioSlice.actions;
